import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
  Paper,
  ButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useBookingActions } from '../../hooks/booking/useBookingActions';
import BookingForm from '../../components/bookings/BookingForm';
import BookingCalendar from './BookingCalendar';
import Notifications from '../../components/common/Notifications';
import { CreateBookingDTO } from '../../types/booking';
import { NotificationT, SEVERITY } from '../../types/notification';
import dayjs from 'dayjs';
import { useAuth } from '../../hooks';
import { FcGoogle } from 'react-icons/fc';
import { useCalendar } from '../../hooks/calendar/useCalendar';
import { calenderEndpoints } from '../../services/api/endpoints/calendar.endpoints';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import BookingRefreshButton from './BookingRefreshButton';
import { useBookings } from '../../hooks/booking/useBookings';
import { paymentEndpoints } from '../../services/api/endpoints/payment.endpoint';
import { formatCurrency } from '../../utils/formatters';

const BookingsView = () => {
  const theme = useTheme();
  const { role, clientResourceId } = useAuth();
  const organization = useSelector((state: RootState) => state.organizations.selectedOrganisation);
  const { createBooking } = useBookingActions();
  const { refetch } = useBookings();

  // TODO: For testing - toggle this to true/false to test payment features
  // Replace with: organization?.isStripeEnabled once backend field is added
  //const isStripeEnabled = true; // TESTING: Set to true to enable payment features
  const isStripeEnabled = organization?.isStripeEnabled || false;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { calendarProvider, calendarrefetch } = useCalendar();
  const [integrationLoading, setIntegrationLoading] = useState<string | null>(null);
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  console.log('[BookingsView] Google Calendar button visibility:', {
    role,
    clientResourceId,
    roleCheck: role !== 'admin',
    resourceCheck: clientResourceId !== null,
    buttonVisible: role !== 'admin' && clientResourceId !== null,
    calendarProvider: calendarProvider
      ? {
          isGoogleCalendarEnabled: calendarProvider.isGoogleCalendarEnabled,
          googleMail: calendarProvider.googleMail,
        }
      : null,
  });

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    invoiceId: string;
    invoiceNumber: string;
    totalAmount: number;
    currency: string;
  }>({
    open: false,
    invoiceId: '',
    invoiceNumber: '',
    totalAmount: 0,
    currency: 'AUD',
  });

  const [selectedSlot, setSelectedSlot] = useState<{ start: string; end: string } | null>(null);

  const handleSlotSelect = (start: Date, end: Date) => {
    setSelectedSlot({
      start: dayjs(start).format('YYYY-MM-DDTHH:mm'),
      end: dayjs(end).format('YYYY-MM-DDTHH:mm'),
    });
    setIsFormOpen(true);
  };

  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: dayjs().startOf('week').toISOString(),
    end: dayjs().endOf('week').toISOString(),
  });

  const [selectedResource, setSelectedResource] = useState<number | undefined>(
    clientResourceId ?? undefined
  );

  const handleDateRangeChange = (start: Date, end: Date) => {
    setDateRange({
      start: dayjs(start).toISOString(),
      end: dayjs(end).toISOString(),
    });
  };

  const handleCreateBooking = async (data: CreateBookingDTO) => {
    try {
      const formattedData = {
        ...data,
        startTime: new Date(data.startTime).toISOString(),
        endTime: new Date(data.endTime).toISOString(),
      };
      const result: any = await createBooking(formattedData);

      // Build success message with meeting link info if present
      const hasMeetingLink = result?.meetingLink;
      const meetingProvider =
        result?.meetingProvider === 'google_meet'
          ? 'Google Meet'
          : result?.meetingProvider === 'zoom'
            ? 'Zoom'
            : '';
      const successMessage = hasMeetingLink
        ? `Booking created successfully. ${meetingProvider} link: ${result.meetingLink}`
        : 'Booking created successfully';

      // Warn if online meeting was expected but link was not generated
      if (result?.meetingProvider && !hasMeetingLink) {
        setNotification({
          open: true,
          message: `Booking created, but ${meetingProvider} meeting link could not be generated. Please check your Meeting Integrations settings.`,
          severity: SEVERITY.WARNING,
        });
        setIsFormOpen(false);
        return;
      }

      // Check if payment is required (Stripe enabled and invoice returned)
      if (isStripeEnabled && organization && result?.invoice) {
        try {
          const invoiceResponse = await paymentEndpoints.getInvoiceById(
            organization.id,
            result.invoice.id.toString()
          );

          setPaymentDialog({
            open: true,
            invoiceId: result.invoice.id.toString(),
            invoiceNumber: invoiceResponse.data.invoiceNumber,
            totalAmount: invoiceResponse.data.totalCents / 100,
            currency: invoiceResponse.data.currency,
          });
        } catch (err) {
          console.error('Failed to fetch invoice:', err);
          setNotification({
            open: true,
            message: successMessage,
            severity: SEVERITY.SUCCESS,
          });
        }
      } else {
        setNotification({
          open: true,
          message: successMessage,
          severity: SEVERITY.SUCCESS,
        });
      }

      setIsFormOpen(false);
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to create booking',
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handlePayNow = async () => {
    if (!organization || !paymentDialog.invoiceId) return;

    try {
      const session = await paymentEndpoints.createPaymentSession(
        organization.id,
        paymentDialog.invoiceId
      );

      window.location.href = session.data.checkoutUrl;
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message || 'Failed to create payment session',
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handlePayLater = () => {
    setPaymentDialog({ ...paymentDialog, open: false });
    setNotification({
      open: true,
      message: 'Booking created. You can pay later from Payment Settings.',
      severity: SEVERITY.INFO,
    });
  };

  const handleIntegration = async () => {
    try {
      setIntegrationLoading('google');
      if (!clientResourceId || !organization) {
        setNotification({
          open: true,
          message: 'Please select a resource before linking Google Calendar',
          severity: SEVERITY.ERROR,
        });
        return;
      }
      const response = await calenderEndpoints.getGoogleAuthUrl(organization.id, clientResourceId);
      if (!response.data) throw new Error('Failed to retrieve Google authentication URL');

      const popup = window.open(
        response.data,
        'GoogleCalendarPopup',
        'width=500,height=600,resizable,scrollbars=yes,status=1'
      );

      if (!popup) {
        alert('Popup blocked! Please allow popups and try again.');
        return;
      }

      // Polling to check when the popup closes
      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed);
          calendarrefetch();
          if (clientResourceId !== null) {
            refetch(clientResourceId, dateRange.start, dateRange.end);
          }
        }
      }, 500);
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to link Google Calendar',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIntegrationLoading(null);
      calendarrefetch();
    }
  };

  const handleUnlink = async () => {
    try {
      setIntegrationLoading('google');
      if (!clientResourceId || !organization) {
        setNotification({
          open: true,
          message: 'Please select a resource before unlinking Google Calendar',
          severity: SEVERITY.ERROR,
        });
        return;
      }
      await calenderEndpoints.unlinkCalendar(organization.id, clientResourceId, 'google');
      setNotification({
        open: true,
        message: 'Google Calendar unlinked successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to unlink Google Calendar',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIntegrationLoading(null);
      calendarrefetch();
      // Refetch bookings after unlinking
      if (clientResourceId !== null) {
        refetch(clientResourceId, dateRange.start, dateRange.end);
      }
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          // border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Bookings
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {role !== 'admin' && clientResourceId !== null && (
              <Tooltip
                title={calendarProvider?.isGoogleCalendarEnabled ? calendarProvider.googleMail : ''}
                placement="top"
              >
                <Button
                  startIcon={<FcGoogle size={16} />}
                  onClick={() => {
                    if (calendarProvider?.isGoogleCalendarEnabled) {
                      setConfirmUnlinkOpen(true);
                    } else {
                      handleIntegration();
                    }
                  }}
                  disabled={integrationLoading === 'google'}
                  sx={{
                    textTransform: 'none',
                    px: 2,
                    py: 1,
                    height: 36,
                    minWidth: { xs: 'auto', sm: 200 },
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: `${theme.palette.primary.main}08`,
                    },
                  }}
                >
                  {integrationLoading === 'google' ? (
                    <CircularProgress size={20} />
                  ) : calendarProvider?.isGoogleCalendarEnabled ? (
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: 150,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {`${calendarProvider.googleMail.split('@')[0]} `}
                    </Typography>
                  ) : (
                    'Google Calendar'
                  )}
                </Button>
              </Tooltip>
            )}
            <ButtonGroup
              variant="outlined"
              size="small"
              sx={{
                '& .MuiButton-root': {
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  height: 36,
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                  },
                },
              }}
            >
              <Button startIcon={<Plus size={16} />} onClick={() => setIsFormOpen(true)}>
                Create
              </Button>
              <BookingRefreshButton selectedResource={selectedResource} dateRange={dateRange} />
            </ButtonGroup>
          </Box>
        </Box>

        <BookingCalendar
          onSlotSelect={handleSlotSelect}
          selectedResource={selectedResource}
          onResourceChange={setSelectedResource}
          onDateChange={handleDateRangeChange}
        />

        <BookingForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCreateBooking}
          selectedSlot={selectedSlot}
        />

        <Dialog open={confirmUnlinkOpen} onClose={() => setConfirmUnlinkOpen(false)}>
          <DialogTitle>Unlink Google Calendar</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to unlink your Google Calendar? Future bookings will no longer
              sync to your calendar.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmUnlinkOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setConfirmUnlinkOpen(false);
                handleUnlink();
              }}
              color="error"
            >
              Unlink
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Dialog - shown when booking requires payment */}
        <Dialog open={paymentDialog.open} onClose={handlePayLater} maxWidth="sm" fullWidth>
          <DialogTitle>Payment Required</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Your booking has been created successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Invoice <strong>{paymentDialog.invoiceNumber}</strong> is ready for payment.
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: theme.palette.grey[100],
                borderRadius: 1,
                my: 2,
              }}
            >
              <Typography variant="h6">
                Total: {formatCurrency(paymentDialog.totalAmount, paymentDialog.currency)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              You can pay now via Stripe Checkout, or pay later from Payment Settings.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePayLater} color="inherit">
              Pay Later
            </Button>
            <Button onClick={handlePayNow} variant="contained" color="primary">
              Pay Now
            </Button>
          </DialogActions>
        </Dialog>

        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Paper>
    </Box>
  );
};

export default BookingsView;
